import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from "../../../styles/projects.module.css";
import { requesturl } from '../../../shared/utils/requesturl';

const PROJECTS_API = `${requesturl}api-projects.php`;
const MEDIA_API = `${requesturl}media.php?file=`;

const Projects = ({ isHomepage = false, maxItems = 3 }) => {
    const [allProjects, setAllProjects] = useState([]);
    const [displayedCount, setDisplayedCount] = useState(isHomepage ? maxItems : 3);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('id');
    const searchQuery = (searchParams.get('search') || '').trim();

    const itemsPerLoad = 3;

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        setDisplayedCount(isHomepage ? maxItems : itemsPerLoad);
    }, [isHomepage, maxItems, searchQuery]);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await fetch(PROJECTS_API);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setAllProjects(data.data || []);
                setError(null);
            } else {
                setError(data.error || 'Failed to fetch projects');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error fetching projects:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredProjects = useMemo(() => {
        if (!searchQuery) {
            return allProjects;
        }

        const normalizedQuery = searchQuery.toLowerCase();
        return allProjects.filter((project) => {
            const title = (project.project_title || '').toLowerCase();
            const description = (project.project_description || '').toLowerCase();
            return title.includes(normalizedQuery) || description.includes(normalizedQuery);
        });
    }, [allProjects, searchQuery]);

    const displayedProjects = useMemo(() => {
        return filteredProjects.slice(0, displayedCount);
    }, [filteredProjects, displayedCount]);

    const handleLoadMore = () => {
        setDisplayedCount((prev) => Math.min(prev + itemsPerLoad, filteredProjects.length));
    };

    const handleProjectClick = (projectIdToView) => {
        const params = new URLSearchParams();
        params.set('id', projectIdToView);
        if (searchQuery) {
            params.set('search', searchQuery);
        }
        navigate(`/projects?${params.toString()}`);
    };

    const handleViewAll = () => {
        navigate('/projects');
    };

    const handleBackToList = () => {
        if (searchQuery) {
            navigate(`/projects?search=${encodeURIComponent(searchQuery)}`);
            return;
        }
        navigate('/projects');
    };

    if (loading) {
        return (
            <div className={styles.projectswrapper}>
                <div className={styles.loading}>Loading projects...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.projectswrapper}>
                <div className={styles.error}>Error: {error}</div>
            </div>
        );
    }

    if (allProjects.length === 0) {
        return (
            <div className={styles.projectswrapper}>
                <div className={styles.empty}>No projects available</div>
            </div>
        );
    }

    const selectedProject = projectId
        ? filteredProjects.find((project) => project.project_id.toString() === projectId)
        : null;

    if (projectId && !selectedProject && !loading) {
        return (
            <div className={styles.projectswrapper}>
                <button onClick={handleBackToList} className={styles.backbtn}>Back to Projects</button>
                <div className={styles.empty}>Project not found for this search.</div>
            </div>
        );
    }

    if (selectedProject) {
        const images = Array.isArray(selectedProject.project_images) ? selectedProject.project_images : [];
        return (
            <div className={styles.projectswrapper}>
                <button onClick={handleBackToList} className={styles.backbtn}>Back to Projects</button>
                <div className={styles.singleproject}>
                    <div className={styles.singleprojectlayout}>
                        <div className={styles.singleprojectcopy}>
                            <h2>{selectedProject.project_title}</h2>
                            <p className={styles.description}>{selectedProject.project_description}</p>
                            <p className={styles.metadata}>
                                Created: {new Date(selectedProject.created_at).toLocaleDateString()}
                            </p>
                        </div>

                        <div className={styles.singleprojectmedia}>
                            {images.length > 0 && (
                                <div className={styles.imagegallery}>
                                    <h3>Project Images ({images.length})</h3>
                                    <div className={styles.imagegrid}>
                                        {images.map((imagePath, idx) => (
                                            <div key={idx} className={styles.imageitem}>
                                                <img
                                                    src={`${MEDIA_API}${encodeURIComponent(imagePath)}`}
                                                    alt={`${selectedProject.project_title} ${idx + 1}`}
                                                    className={styles.image}
                                                    onError={(e) => {
                                                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="16"%3EImage Not Found%3C/text%3E%3C/svg%3E';
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (filteredProjects.length === 0) {
        return (
            <div className={styles.projectswrapper}>
                <div className={styles.empty}>No projects matched "{searchQuery}".</div>
            </div>
        );
    }

    const hasMore = displayedCount < filteredProjects.length;

    return (
        <div className={styles.projectswrapper}>
            <div className={styles.sectionheader}>
                <h2 className={styles.heading}>
                    {searchQuery ? `Project Search Results for "${searchQuery}"` : 'Our Projects'}
                </h2>
            </div>

            <div className={styles.projectscontainer}>
                {displayedProjects.map((project) => (
                    <div
                        key={project.project_id}
                        className={styles.projectcard}
                    >
                        <div className={styles.projectimages}>
                            {project.project_images && project.project_images.length > 0 ? (
                                <div className={styles.imagegallery}>
                                    {project.project_images.slice(0, 3).map((image, index) => (
                                        <div key={index} className={styles.imagecontainer}>
                                            <img
                                                src={`${MEDIA_API}${encodeURIComponent(image)}`}
                                                alt={`${project.project_title} - ${index + 1}`}
                                                className={styles.projectimage}
                                                onError={(e) => {
                                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="16"%3EImage Not Found%3C/text%3E%3C/svg%3E';
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className={styles.noimages}>No images</div>
                            )}
                        </div>
                        <div className={styles.projectcontent}>
                            <h3 className={styles.projecttitle}>{project.project_title}</h3>
                            <p className={styles.projectdescription}>{project.project_description.substring(0, 100)}...</p>
                            {project.project_images && project.project_images.length > 0 && (
                                <p className={styles.imagecount}>{project.project_images.length} images</p>
                            )}
                            <p className={styles.projectmeta}>
                                Created: {new Date(project.created_at).toLocaleDateString()}
                            </p>
                            <button
                                className={styles.viewdetailsbtn}
                                onClick={() => handleProjectClick(project.project_id)}
                            >
                                View Details
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {isHomepage && (
                <div className={styles.sectionfooter}>
                    <button type="button" onClick={handleViewAll} className={styles.viewallbtn}>View All</button>
                </div>
            )}

            {!isHomepage && hasMore && (
                <div className={styles.paginationcontainer}>
                    <button onClick={handleLoadMore} className={styles.loadmorebtn}>
                        Load More Projects ({displayedProjects.length} of {filteredProjects.length})
                    </button>
                </div>
            )}
        </div>
    );
};

export default Projects;
